import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "../api/rest";
import type { ParticipantsStats, WalletTagCount } from "../api/types";

/**
 * Словарь ручных пометок и их правка.
 *
 * # Почему словарь опрашивается редко
 *
 * Потому что меняет его только рука оператора, и таких нажатий единицы за смену.
 * Минута — это компромисс с ВТОРОЙ ВКЛАДКОЙ: терминал открывают в двух окнах, и
 * тег, поставленный в одном, должен рано или поздно появиться в списке фильтра
 * другого. Держать ради этого секундную петлю рядом со стаканом незачем.
 *
 * Правка при этом кладёт ответ в кэш сразу — ждать минуту после собственного
 * нажатия оператор не должен.
 */
const DICT_POLL_MS = 60_000;

export function walletTagsKey(): readonly unknown[] {
  return ["wallet-tags"];
}

export function useWalletTagDict() {
  return useQuery<WalletTagCount[]>({
    queryKey: walletTagsKey(),
    queryFn: async () => (await api.walletTags()).tags,
    refetchInterval: DICT_POLL_MS,
    // Прошлый словарь остаётся на экране, пока едет новый: список фильтра — это
    // кнопки, и мигание пустотой сбрасывало бы прицел оператора в момент выбора.
    placeholderData: (prev) => prev,
  });
}

/**
 * Постановка и снятие пометок на группе кошельков.
 *
 * # Что делает onSuccess и почему двумя действиями
 *
 * ПЕРВОЕ: чипы в уже нарисованных строках правятся немедленно, прямо в кэше
 * панели. Панель опрашивается раз в пять–пятнадцать секунд, и без этого оператор
 * жал бы «поставить» и до следующего ответа видел строку без пометки — то есть
 * не знал бы, сработало ли нажатие.
 *
 * ВТОРОЕ: панель всё равно перечитывается. Правка меняет не только чипы: под
 * включённым фильтром помеченный кошелёк обязан ВОЙТИ в таблицу, а снятый —
 * уйти, а решает это сервер, потому что фильтр применяется до отсечки в сто
 * строк. Обойтись одной правкой кэша значило бы показать строку, которой при
 * текущем фильтре там не место.
 */
export function useApplyWalletTags() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (req: {
      addresses: readonly string[];
      add?: readonly string[];
      remove?: readonly string[];
    }) => api.applyWalletTags(req),
    onSuccess: (res) => {
      // Словарь приезжает тем же ответом: без него список фильтра до следующего
      // опроса не знал бы о только что созданной пометке.
      //
      // Проверка на `null`, а НЕ на пустоту: пустой массив — законный ответ
      // «пометок не осталось ни одной», и он приходит ровно тогда, когда снят
      // последний тег. Прими мы его за отказ — снятая пометка осталась бы в
      // списке фильтра, и по ней можно было бы отфильтровать в пустоту.
      if (res.tags !== null) {
        client.setQueryData(walletTagsKey(), res.tags);
      }
      client.setQueriesData<ParticipantsStats>(
        { queryKey: ["participants"] },
        (prev) => patchTags(prev, res.wallets),
      );
      void client.invalidateQueries({ queryKey: ["participants"] });
    },
  });
}

/**
 * Подставить новые пометки в уже лежащий в кэше свод.
 *
 * Отдельной функцией, а не лямбдой внутри: это единственное место, где ответ
 * правки и ответ панели встречаются, и ключ там разного регистра — свод отдаёт
 * адрес как пришёл от индексатора, а правка отвечает нижним.
 */
function patchTags(
  prev: ParticipantsStats | undefined,
  wallets: Record<string, string[]>,
): ParticipantsStats | undefined {
  if (!prev) return prev;
  let touched = false;
  const rows = prev.rows.map((row) => {
    const tags = wallets[row.wallet.toLowerCase()];
    if (!tags) return row;
    touched = true;
    return { ...row, tags };
  });
  // Ни одной строки не тронуто — возвращаем ПРЕЖНИЙ объект, а не копию: новая
  // ссылка перерисовала бы таблицу на сотню строк ни за чем.
  return touched ? { ...prev, rows } : prev;
}
